import type { ReactElement } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink, Phone, PhoneOff, Ticket } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
// Импортируем из re-export WebSocketProvider (а не напрямую из websocket-context):
// все потребители useWebSocket должны брать Context из одного модуля, иначе
// Fast Refresh создаёт две ссылки на Context и потомки кидают
// "useWebSocket must be used within a WebSocketProvider" (см. apps/admin/CLAUDE.md).
import { useWebSocket } from "@/providers/WebSocketProvider";

interface IncomingCall {
  roomId: string;
  callerName: string | null;
  ticketId: number | null;
}

// Автоматически скрыть модалку, если админ не отреагировал — звонок к тому
// моменту, скорее всего, уже отменён или принят другим админом.
const AUTO_DISMISS_MS = 45_000;

/**
 * Глобальная модалка входящего звонка в техподдержку. Слушает admin WS
 * (/ws/notifications) на событие 'support.call.incoming' (его шлёт бэк из
 * CallsService.createRoom при звонке в SUPPORT-чат). Кнопка «Войти» открывает
 * существующую LiveKit-комнату админки (роут /livestreams/stage/:roomId) в
 * новой вкладке — там StageRoomPage сам возьмёт admin-токен.
 *
 * Несколько админов могут принять один звонок: каждый получает уникальный
 * LiveKit identity (admin-<adminId>), конфликтов нет.
 */
export function IncomingSupportCallModal(): ReactElement | null {
  const { lastMessage } = useWebSocket();
  const [call, setCall] = useState<IncomingCall | null>(null);
  // Дедуп: не показывать повторно ту же комнату (WS-reconnect может вернуть
  // тот же lastMessage по ссылке, плюс защита от дублей broadcast'а).
  const seenRef = useRef<Set<string>>(new Set());
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!lastMessage || lastMessage.type !== "support.call.incoming") {
      return;
    }
    if (seenRef.current.has(lastMessage.roomId)) {
      return;
    }
    seenRef.current.add(lastMessage.roomId);
    setCall({
      roomId: lastMessage.roomId,
      callerName: lastMessage.callerName,
      ticketId: lastMessage.ticketId,
    });

    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
    }
    dismissTimerRef.current = setTimeout(() => {
      setCall(null);
    }, AUTO_DISMISS_MS);
  }, [lastMessage]);

  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
    };
  }, []);

  // Рингтон: играет в цикле пока показывается модалка входящего звонка.
  // Останавливается при снятии call (вход в комнату / закрытие / авто-dismiss).
  // autoplay может быть заблокирован без user-gesture — .play() тихо отклоняется.
  useEffect(() => {
    if (!call) {
      return;
    }
    const audio = new Audio("/sounds/ringtone.mp3");
    audio.loop = true;
    audio.volume = 0.6;
    void audio.play().catch(() => {});
    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, [call]);

  const handleClose = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
    }
    setCall(null);
  }, []);

  const handleJoin = useCallback(() => {
    if (!call) {
      return;
    }
    const label = call.callerName
      ? `Поддержка: ${call.callerName}`
      : "Поддержка";
    window.open(
      `/livestreams/stage/${call.roomId}?label=${encodeURIComponent(label)}`,
      "_blank",
    );
    handleClose();
  }, [call, handleClose]);

  const handleOpenTicket = useCallback(() => {
    if (!call?.ticketId) {
      return;
    }
    window.open(`/support-tickets/show/${call.ticketId}`, "_blank");
  }, [call]);

  if (!call) {
    return null;
  }

  const callerLabel = call.callerName ?? "Пользователь";

  return (
    <Dialog open onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-green-500/15 text-green-600">
              <Phone className="h-4 w-4 animate-pulse" />
            </span>
            Входящий звонок в поддержку
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{callerLabel}</span>
            {" "}звонит в техническую поддержку.
          </DialogDescription>
        </DialogHeader>

        {call.ticketId !== null && (
          <button
            type="button"
            onClick={handleOpenTicket}
            className="flex items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
          >
            <span className="flex items-center gap-2">
              <Ticket className="h-4 w-4 text-muted-foreground" />
              <span>
                Заявка{" "}
                <span className="font-semibold">#{call.ticketId}</span>
              </span>
            </span>
            <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={handleClose}>
            <PhoneOff className="mr-2 h-4 w-4" />
            Закрыть
          </Button>
          <Button onClick={handleJoin}>
            <Phone className="mr-2 h-4 w-4" />
            Войти в комнату
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
